<div class="email-template-preview">
    <!-- Template Not Found Overlay -->
    @if (templateNotFound()) {
    <div class="template-not-found-overlay">
        <div class="not-found-card">
            <div class="not-found-icon">
                <mat-icon>search_off</mat-icon>
            </div>
            <h3>Template Not Available</h3>
            <p>The corresponding email template for this published item could not be found.</p>
            <button class="btn-close-overlay" (click)="closeDialog()">
                <mat-icon>close</mat-icon>
                <span>Close</span>
            </button>
        </div>
    </div>
    }

    <!-- Header -->
    <div class="preview-header">
        <div class="header-left">
            <div class="header-icon-wrapper">
                <mat-icon class="header-icon">email</mat-icon>
            </div>
            <div class="header-title-section">
                <h2 class="template-title">{{ templateName() }}</h2>
                @if (data.isPublishedVersion && data.publishedAt) {
                <span class="template-subtitle">
                    <mat-icon class="subtitle-icon">history</mat-icon>
                    Published Version
                    <span class="publish-date">({{ data.publishedAt | date:'medium' }})</span>
                </span>
                } @else {
                <span class="template-subtitle">Email Preview</span>
                }
            </div>
        </div>

        <div class="header-controls">
            <!-- View Mode Toggles (Icons Only) -->
            <div class="view-toggles">
                <button class="toggle-btn icon-only" [class.active]="viewMode() === 'desktop'"
                    (click)="setViewMode('desktop')" matTooltip="Desktop view">
                    <mat-icon>desktop_windows</mat-icon>
                </button>
                <button class="toggle-btn icon-only" [class.active]="viewMode() === 'mobile'"
                    (click)="setViewMode('mobile')" matTooltip="Mobile view">
                    <mat-icon>smartphone</mat-icon>
                </button>
            </div>

            <div class="divider"></div>

            <!-- Variable Values Toggle -->
            @if (hasVariables()) {
            <button class="variable-toggle" [class.active]="showVariableValues()" (click)="toggleVariableValues()"
                [matTooltip]="showVariableValues() ? 'Show Placeholders' : 'Show Variable Values'">
                <mat-icon>{{ showVariableValues() ? 'code_off' : 'data_object' }}</mat-icon>
            </button>

            <div class="divider"></div>
            }

            <!-- Fullscreen Toggle -->
            <button class="control-btn" (click)="toggleFullscreen()" matTooltip="Fullscreen preview">
                <mat-icon>fullscreen</mat-icon>
            </button>

            <div class="divider"></div>

            <!-- Theme Toggle -->
            <button class="theme-toggle" [class.dark]="isDarkMode()" (click)="toggleTheme()"
                [matTooltip]="isDarkMode() ? 'Switch to Light Mode' : 'Switch to Dark Mode'">
                <mat-icon>{{ isDarkMode() ? 'light_mode' : 'dark_mode' }}</mat-icon>
            </button>

            <div class="divider"></div>

            <!-- Test Email Button -->
            <button class="control-btn" (click)="openTestEmailDialog()" matTooltip="Send test email">
                <mat-icon>send</mat-icon>
            </button>

            <div class="divider"></div>

            <!-- Close Button -->
            <button class="close-btn" (click)="closeDialog()" matTooltip="Close preview">
                <mat-icon>close</mat-icon>
            </button>
        </div>
    </div>

    <!-- Preview Area -->
    <div class="preview-body" [class.dark-mode]="isDarkMode()">
        <div class="preview-container" [class.mobile-view]="viewMode() === 'mobile'">

            <!-- No Content State -->
            @if (hasNoContent()) {
            <div class="no-content-state">
                <div class="no-content-card">
                    <div class="no-content-icon">
                        <mat-icon>content_paste_off</mat-icon>
                    </div>
                    <h3>No Content Available</h3>
                    <p>
                        @if (data.isPublishedVersion) {
                        The <strong>{{ selectedLang() | uppercase }}</strong> language was not included when this template was published.
                        <br><br>
                        To add this language, go to the Email Editor, add the content for {{ selectedLang() | uppercase }}, and publish the template again.
                        } @else {
                        No content has been added for the <strong>{{ selectedLang() | uppercase }}</strong> language yet.
                        }
                    </p>
                </div>
            </div>
            } @else {
            <!-- Device Frame -->
            <div class="device-frame">
                <!-- Email Client UI Wrapper -->
                <div class="email-client-ui">
                    <!-- Email Metadata Header -->
                    <div class="email-meta-header">
                        <div class="subject-line">{{ subject() }}</div>
                        <div class="sender-info">
                            <div class="avatar">
                                <span>M</span>
                            </div>
                            <div class="meta-details">
                                <div class="from-line">
                                    <span class="name">Mevolve</span>
                                    <span class="email">&lt;info&#64;mevolve.app&gt;</span>
                                </div>
                                <div class="to-line">to me</div>
                            </div>
                            <div class="date-info">
                                {{ 0 | date:'shortTime' }}
                            </div>
                        </div>
                    </div>

                    <!-- Email Content -->
                    <div class="email-content-wrapper">
                        <iframe [srcdoc]="safeHtmlForIframe()" title="Email Preview" sandbox="allow-same-origin"
                            class="email-iframe">
                        </iframe>
                    </div>
                </div>
            </div>
            }
        </div>
    </div>

    <!-- Fullscreen Preview Overlay -->
    @if (isFullscreen()) {
    <div class="fullscreen-preview-overlay" [class.dark-mode]="isDarkMode()">
        <div class="fullscreen-header">
            <div class="header-left">
                <div class="header-icon-wrapper">
                    <mat-icon class="header-icon">email</mat-icon>
                </div>
                <div class="header-title-section">
                    <h2 class="template-title">{{ templateName() }}</h2>
                    <span class="template-subtitle">Fullscreen Preview</span>
                </div>
            </div>

            <div class="header-controls">
                <!-- Dimension Controls -->
                <div class="dimension-controls">
                    <label>W</label>
                    <input type="number" [value]="customWidth()"
                        (input)="updateDimensions(+$any($event.target).value, customHeight())" min="320" max="1920" />
                    <span class="dimension-separator">×</span>
                    <label>H</label>
                    <input type="number" [value]="customHeight()"
                        (input)="updateDimensions(customWidth(), +$any($event.target).value)" min="480" max="1080" />
                </div>

                <!-- Preset Sizes -->
                <div class="preset-sizes">
                    <button class="preset-btn" (click)="updateDimensions(375, 667)" matTooltip="iPhone SE">SE</button>
                    <button class="preset-btn" (click)="updateDimensions(390, 844)" matTooltip="iPhone 14">14</button>
                    <button class="preset-btn" (click)="updateDimensions(768, 1024)" matTooltip="iPad">iPad</button>
                    <button class="preset-btn" (click)="updateDimensions(1280, 720)" matTooltip="HD">HD</button>
                    <button class="preset-btn" (click)="updateDimensions(1920, 1080)" matTooltip="Full HD">FHD</button>
                </div>

                <div class="divider"></div>

                <!-- Dark Mode Toggle -->
                <button class="theme-toggle" [class.dark]="isDarkMode()" (click)="toggleTheme()"
                    [matTooltip]="isDarkMode() ? 'Switch to Light Mode' : 'Switch to Dark Mode'">
                    <mat-icon>{{ isDarkMode() ? 'light_mode' : 'dark_mode' }}</mat-icon>
                </button>

                <div class="divider"></div>

                <!-- Test Email Button -->
                <button class="control-btn" (click)="openTestEmailDialog()" matTooltip="Send test email">
                    <mat-icon>send</mat-icon>
                </button>

                <div class="divider"></div>

                <!-- Close Fullscreen -->
                <button class="close-btn" (click)="closeFullscreen()" matTooltip="Exit fullscreen">
                    <mat-icon>close</mat-icon>
                </button>
            </div>
        </div>

        <div class="fullscreen-body">
            <div class="preview-frame" [style.width.px]="customWidth()" [style.height.px]="customHeight()">
                <!-- Device Frame -->
                <div class="device-frame">
                    <!-- Email Client UI -->
                    <div class="email-client-ui">
                        <div class="email-meta-header">
                            <div class="subject-line">{{ subject() }}</div>
                            <div class="sender-info">
                                <div class="avatar">
                                    <span>M</span>
                                </div>
                                <div class="meta-details">
                                    <div class="from-line">
                                        <span class="name">Mevolve</span>
                                        <span class="email">&lt;info&#64;mevolve.app&gt;</span>
                                    </div>
                                    <div class="to-line">to me</div>
                                </div>
                                <div class="date-info">
                                    {{ 0 | date:'shortTime' }}
                                </div>
                            </div>
                        </div>

                        <div class="email-content-wrapper">
                            <iframe [srcdoc]="safeHtmlForIframe()" title="Email Preview" sandbox="allow-same-origin"
                                class="email-iframe">
                            </iframe>
                        </div>
                    </div>
                </div>
            </div>

            <div class="dimension-indicator">
                {{ customWidth() }} × {{ customHeight() }}
            </div>
        </div>
    </div>
    }
</div>